<template>
  <div
    v-if="variants.length"
    class="v2-product-variants"
  >
    <div class="v2-product-variants__table-wrap">
      <table class="v2-product-variants__table">
        <thead>
          <tr>
            <th class="v2-product-variants__th v2-product-variants__th--sticky">
              Color/Size
            </th>
            <th
              v-for="heading in sizeHeadings"
              :key="heading"
              class="v2-product-variants__th"
            >
              {{ heading }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(variant, vIdx) in variants"
            :key="vIdx"
            class="v2-product-variants__row"
          >
            <td class="v2-product-variants__td v2-product-variants__td--sticky v2-product-variants__color-cell">
              {{ variant[COLOR] }}
            </td>
            <td
              v-for="heading in sizeHeadings"
              :key="heading"
              class="v2-product-variants__td"
            >
              <div class="v2-product-variants__input-wrap">
                <input
                  v-model.number="variant[heading].value"
                  type="number"
                  class="v2-product-variants__input"
                  :class="getInputClasses(variant, heading)"
                  min="0"
                  :max="getMaxQuantity(variant[heading])"
                  :readonly="isDisabled || (isProductValueDisabled(variant[heading].quantityAvailable) && variant[heading].maxShipmentQty == null)"
                  @input="onQuantityInput(variant[heading])"
                >
                <div
                  v-if="shouldShowMaxQty(variant, heading)"
                  class="v2-product-variants__max-qty"
                >
                  Max Qty: {{ getDisplayMaxQty(variant[heading]) }}
                </div>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import {
  getVariants,
  hasProductMaxQuantity,
  isProductItemValid,
  isProductValueDisabled,
} from '@/@core/utils/utils'
import {
  hasShipmentMaxQty as hasShipmentMaxQtyUtil,
  isQuantityExceedError as isQuantityExceedErrorUtil,
  isShipmentQtyExceedError as isShipmentQtyExceedErrorUtil,
  getDisplayMaxQtyValue,
} from '@/views/apps/product/detail/productVariantValidation'

export default {
  name: 'V2ProductVariants',
  props: {
    items: {
      type: Array,
      required: true,
      default: () => [],
    },
    isCarton: {
      type: Boolean,
      required: true,
    },
    isModifyEnabledBrand: {
      type: Boolean,
      default: false,
    },
    isEditEnabled: {
      type: Boolean,
      default: false,
    },
    disabledProduct: {
      type: Boolean,
      default: false,
    },
    minQtyPerSize: {
      type: Number,
      default: 0,
    },
    isShipmentSettingsModal: {
      type: Boolean,
      default: false,
    },
    showQuantityExceedValidation: {
      type: Boolean,
      default: true,
    },
    isGuest: {
      type: Boolean,
      default: false,
    },
    isCancelled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    const COLOR = 'color'
    return {
      headings: [COLOR],
      variants: [],
      COLOR,
    }
  },
  computed: {
    isDisabled() {
      return this.isCarton || this.isCancelled || this.isGuest || this.disabledProduct || (!this.isEditEnabled && !this.isModifyEnabledBrand)
    },
    canShowMaxQty() {
      return !this.isCarton && (this.isModifyEnabledBrand || this.isEditEnabled)
    },
    sizeHeadings() {
      return this.headings.filter(h => h !== this.COLOR)
    },
  },
  mounted() {
    this.initializeVariants(this.items)
  },
  methods: {
    isProductValueDisabled,
    initializeVariants(productItems) {
      const { headings, variants } = getVariants(productItems, this.headings, this.COLOR)
      if (productItems.length) {
        this.headings = headings
        this.variants = variants
        this.$emit('update-variants', this.variants)
      }
    },
    hasShipmentMaxQty(item) {
      return hasShipmentMaxQtyUtil(this.isShipmentSettingsModal, item)
    },
    shouldShowMaxQty(variant, heading) {
      if (this.isShipmentSettingsModal && this.isCarton) return false
      const item = variant?.[heading]
      if (!item) return false
      if (this.hasShipmentMaxQty(item)) return true
      if (!this.canShowMaxQty) return false
      return hasProductMaxQuantity(item.quantityAvailable)
    },
    getInputClasses(variant, heading) {
      const item = variant?.[heading]
      if (!item) return {}
      const isDecimalError = !Number.isInteger(item.value)
      const isMinQtyError = !this.isCarton && item.value != null && item.value < this.minQtyPerSize
      const isMaxQtyError = isQuantityExceedErrorUtil({
        showValidation: this.showQuantityExceedValidation,
        isCarton: this.isCarton,
        hasShipmentMax: this.hasShipmentMaxQty(item),
        isProductItemValid,
        item,
      })
      const isShipmentQtyError = isShipmentQtyExceedErrorUtil(this.showQuantityExceedValidation, item)
      return {
        'v2-product-variants__input--error': isDecimalError || isMinQtyError || isMaxQtyError || isShipmentQtyError,
      }
    },
    getDisplayMaxQty(item) {
      return getDisplayMaxQtyValue(this.hasShipmentMaxQty(item), item)
    },
    getMaxQuantity(item) {
      if (item.maxShipmentQty !== undefined) return item.maxShipmentQty
      if (this.canShowMaxQty && item.quantityAvailable !== undefined) return item.quantityAvailable
      return undefined
    },
    onQuantityInput(item) {
      if (typeof item.value === 'number' && item.value < 0) item.value = 0
      this.$forceUpdate()
      this.$emit('update-variants', this.variants)
    },
  },
}
</script>

<style lang="scss" scoped>
.v2-product-variants {
  margin-top: var(--v2-space-4);

  // Glass variant table — same recipe as the cart-page variant table for
  // visual consistency. Header uses surface-1 (stronger tint) to read as
  // "header row", body uses surface-2 (lighter tint).
  &__table-wrap {
    display: inline-block;
    overflow-x: auto;
    border: 1px solid var(--v2-glass-stroke);
    border-radius: var(--v2-radius-lg);
    max-width: 100%;
  }

  &__table {
    border-collapse: collapse;
    font-size: 0.875rem;
    font-family: var(--v2-font-sans);
  }

  &__th {
    padding: var(--v2-space-2) var(--v2-space-3);
    text-align: center;
    font-weight: 600;
    color: var(--v2-muted-foreground);
    background: var(--v2-glass-surface-1);
    border-bottom: 1px solid var(--v2-glass-stroke);
    white-space: nowrap;
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;

    &--sticky {
      position: sticky;
      left: 0;
      z-index: 2;
      background: var(--v2-glass-surface-1);
      text-align: left;
    }
  }

  &__row {
    &:not(:last-child) .v2-product-variants__td {
      border-bottom: 1px solid var(--v2-glass-stroke);
    }
  }

  &__td {
    padding: var(--v2-space-2) var(--v2-space-3);
    vertical-align: middle;
    text-align: center;
    background: var(--v2-glass-surface-2);

    &--sticky {
      position: sticky;
      left: 0;
      z-index: 1;
      background: var(--v2-glass-surface-2);
      text-align: left;
    }
  }

  &__color-cell {
    font-weight: 500;
    color: var(--v2-foreground);
    white-space: nowrap;
    min-width: 80px;
  }

  &__input-wrap {
    display: inline-flex;
    flex-direction: column;
    align-items: center;
  }

  // Glass qty input — matches V2Input recipe so cells stay visually
  // consistent with the rest of the form controls in v2.
  &__input {
    width: 70px;
    padding: var(--v2-space-1) var(--v2-space-2);
    border: 1px solid var(--v2-glass-stroke);
    border-radius: var(--v2-radius-sm);
    font-size: 0.875rem;
    font-family: var(--v2-font-sans);
    color: var(--v2-foreground);
    background: var(--v2-glass-surface-2);
    text-align: center;
    transition: background 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease;

    &:hover:not(:read-only):not(:focus) {
      border-color: var(--v2-glass-stroke-strong);
    }

    &:focus {
      outline: none;
      border-color: var(--v2-primary);
      box-shadow: 0 0 0 3px oklch(0.62 0.21 295 / 0.25);
    }

    &:read-only {
      background: var(--v2-muted);
      backdrop-filter: none;
      cursor: not-allowed;
      color: var(--v2-muted-foreground);
    }

    &--error {
      border-color: var(--v2-destructive) !important;
      border-width: 2px;

      &:focus {
        box-shadow: 0 0 0 2px rgba(229, 72, 77, 0.2);
      }
    }

    /* Hide number input spinners */
    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
    -moz-appearance: textfield;
  }

  &__max-qty {
    font-size: 0.6875rem;
    color: var(--v2-text-muted-secondary);
    white-space: nowrap;
    margin-top: var(--v2-space-1);
  }
}
</style>
